import { useState } from 'react';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import {
  Edit,
  Delete,
  MoreVert,
  Category,
  CheckCircle,
  Cancel,
  Extension,
  Folder,
  Add,
  Close,
} from '@mui/icons-material';
import { useModuleTypes, useDeleteModuleType, useCreateModuleType, useUpdateModuleType } from '../../hooks/useSuperAdmin';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useQueryClient } from '@tanstack/react-query';
import { colors } from '../../theme/theme';

export const ModuleTypesPage = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedType, setSelectedType] = useState<any>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '' });

  const { moduleTypes, isLoading, error } = useModuleTypes(page + 1);
  const deleteModuleType = useDeleteModuleType();
  const createModuleType = useCreateModuleType();
  const updateModuleType = useUpdateModuleType();

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, type: any) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedType(type);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedType(null);
  };

  const handleDelete = async () => {
    if (!selectedType) return;

    if (window.confirm(`Are you sure you want to delete module type "${selectedType.name}"?`)) {
      try {
        await deleteModuleType.mutateAsync(selectedType.id);
        showSnackbar('Module type deleted successfully', 'success');
        queryClient.invalidateQueries({ queryKey: ['superadmin', 'module-types'] });
        handleMenuClose();
      } catch (error) {
        showSnackbar('Failed to delete module type', 'error');
      }
    }
  };

  const handleOpenAddDialog = () => {
    setFormData({ name: '' });
    setAddDialogOpen(true);
  };

  const handleOpenEditDialog = () => {
    if (!selectedType) return;
    setFormData({ name: selectedType.name || '' });
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleCloseDialogs = () => {
    setAddDialogOpen(false);
    setEditDialogOpen(false);
    setFormData({ name: '' });
  };

  const handleSubmitAdd = async () => {
    try {
      await createModuleType.mutateAsync(formData);
      showSnackbar('Module type created successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'module-types'] });
      handleCloseDialogs();
    } catch (error) {
      showSnackbar('Failed to create module type', 'error');
    }
  };

  const handleSubmitEdit = async () => {
    if (!selectedType) return;
    try {
      await updateModuleType.mutateAsync({ id: selectedType.id, data: formData });
      showSnackbar('Module type updated successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['superadmin', 'module-types'] });
      handleCloseDialogs();
    } catch (error) {
      showSnackbar('Failed to update module type', 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'icon',
      headerName: 'Icon',
      width: 80,
      renderCell: (params) => (
        <Avatar
          src={params.value}
          sx={{ bgcolor: colors.primary.main, width: 36, height: 36 }}
        >
          {params.value ? null : <Category sx={{ fontSize: 20 }} />}
        </Avatar>
      ),
    },
    {
      field: 'name',
      headerName: 'Type Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Extension sx={{ fontSize: 18, color: colors.secondary.main }} />
          <Box>
            <Box sx={{ fontWeight: 600 }}>{params.value}</Box>
            <Box sx={{ fontSize: '0.75rem', color: colors.grey[600] }}>
              {params.row.code || 'No code'}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ color: colors.grey[600], fontSize: '0.875rem' }}>
          {params.value || 'No description'}
        </Box>
      ),
    },
    {
      field: 'modules_count',
      headerName: 'Modules',
      width: 110,
      valueGetter: (value) => value || 0,
      renderCell: (params) => (
        <Chip
          icon={<Folder sx={{ fontSize: 14 }} />}
          label={`${params.value} module${params.value !== 1 ? 's' : ''}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => {
        const isActive = params.value !== 'inactive';
        return (
          <Chip
            icon={isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueGetter: (value) => (value ? new Date(value).toLocaleDateString() : 'N/A'),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Module Types"
          subtitle="Manage module type classifications"
          breadcrumbs={[
            { label: 'Dashboard', path: '/superadmin/dashboard' },
            { label: 'Configuration' },
            { label: 'Module Types' },
          ]}
          action={
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleOpenAddDialog}
              sx={{
                background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${colors.secondary.dark} 0%, ${colors.secondary.main} 100%)`,
                },
              }}
            >
              Add Module Type
            </Button>
          }
        />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load module types. Please try again.
          </Alert>
        )}

        {/* Summary Cards */}
        {!isLoading && moduleTypes && (
          <Grid container spacing={2.5} mb={3}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.secondary.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.secondary.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Total Types</Box>
                    <Category sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {moduleTypes.meta?.total?.toLocaleString() || moduleTypes.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Module classifications</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.success.main} 0%, ${colors.success.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.success.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.success.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Active</Box>
                    <CheckCircle sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {moduleTypes.data?.filter((t: any) => t.status !== 'inactive').length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Currently active</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.primary.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.primary.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>With Modules</Box>
                    <Extension sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {moduleTypes.data?.filter((t: any) => (t.modules_count || 0) > 0).length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>Has modules</Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card
                elevation={0}
                sx={{
                  background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.dark} 100%)`,
                  color: 'white',
                  borderRadius: 2.5,
                  border: `1px solid ${colors.info.dark}`,
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px -10px ${colors.info.main}80`,
                  },
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ opacity: 0.95, fontSize: '0.875rem', fontWeight: 500 }}>Showing</Box>
                    <Folder sx={{ fontSize: 24, opacity: 0.9 }} />
                  </Box>
                  <Box sx={{ fontWeight: 700, fontSize: '2rem', mb: 0.5 }}>
                    {moduleTypes.data?.length || 0}
                  </Box>
                  <Box sx={{ opacity: 0.8, fontSize: '0.75rem' }}>On current page</Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            overflow: 'hidden',
            border: `1px solid ${colors.grey[200]}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            {isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={moduleTypes?.data || []}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50, 100]}
                  paginationModel={{ page, pageSize }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  rowCount={moduleTypes?.meta?.total || moduleTypes?.data?.length || 0}
                  paginationMode={moduleTypes?.meta?.total ? 'server' : 'client'}
                  disableRowSelectionOnClick
                  autoHeight
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-toolbarContainer': {
                      padding: '16px 20px',
                      borderBottom: `1px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${colors.grey[100]}`,
                      fontSize: '0.875rem',
                      py: 1.5,
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: colors.grey[50],
                      borderBottom: `2px solid ${colors.grey[300]}`,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: colors.grey[700],
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    },
                    '& .MuiDataGrid-row': {
                      transition: 'background-color 0.15s ease',
                      '&:hover': {
                        backgroundColor: `${colors.secondary.main}08`,
                        cursor: 'pointer',
                      },
                      '&:nth-of-type(even)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.02)',
                      },
                    },
                    '& .MuiDataGrid-footerContainer': {
                      borderTop: `2px solid ${colors.grey[200]}`,
                      backgroundColor: colors.grey[50],
                      minHeight: '56px',
                    },
                  }}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <MenuItem onClick={handleOpenEditDialog}>
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Module Type</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: colors.error.main }}>
            <ListItemIcon>
              <Delete fontSize="small" sx={{ color: colors.error.main }} />
            </ListItemIcon>
            <ListItemText>Delete Module Type</ListItemText>
          </MenuItem>
        </Menu>

        {/* Add/Edit Dialog */}
        <Dialog
          open={addDialogOpen || editDialogOpen}
          onClose={handleCloseDialogs}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Category />
                </Box>
                <Box>
                  <Box sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                    {editDialogOpen ? 'Edit Module Type' : 'Add Module Type'}
                  </Box>
                  <Box sx={{ fontSize: '0.875rem', color: colors.grey[600] }}>
                    Module type classification
                  </Box>
                </Box>
              </Box>
              <IconButton onClick={handleCloseDialogs} size="small">
                <Close />
              </IconButton>
            </Box>
          </DialogTitle>
          <Divider />
          <DialogContent sx={{ pt: 3 }}>
            <TextField
              label="Module Type Name"
              value={formData.name}
              onChange={(e) => setFormData({ name: e.target.value })}
              fullWidth
              required
              autoFocus
              placeholder="e.g., Business Permit, Land Rates, etc."
              helperText="Enter a descriptive name for this module type"
            />
          </DialogContent>
          <Divider />
          <DialogActions sx={{ p: 2.5 }}>
            <Button onClick={handleCloseDialogs} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={editDialogOpen ? handleSubmitEdit : handleSubmitAdd}
              disabled={!formData.name.trim()}
              sx={{
                background: `linear-gradient(135deg, ${colors.secondary.main} 0%, ${colors.secondary.dark} 100%)`,
              }}
            >
              {editDialogOpen ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AdminLayout>
  );
};
